import {useContext, useEffect, useState} from 'react';

import {Box, Grid, Slider, Typography} from '@mui/material';

import {LanguageContext} from '../../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../../../../../../layouts/theme/designSystem';
import {Answer} from '../../../../../../../lib/api/RequestBodies/features/company/Questionnaires/QuestionnaireRequestBodies';
import {HappinessItem, Item} from '../../../types/SurveyQuestionnaireTypes';

interface QuestionProps {
  data: Item | HappinessItem;
  onAnswer: (event: any) => void;
  currentAnswer?: Answer;
  usageCase?: string;
}

export const Question = ({data, onAnswer, currentAnswer, usageCase}: QuestionProps) => {
  const currentLanguage = useContext(LanguageContext);
  const [sliderValue, setSliderValue] = useState(0);
  const isAnswered = Boolean(currentAnswer);
  const shouldGreyOutUntilAnswered = usageCase === 'b2bLight' && !isAnswered;

  useEffect(() => {
    if (currentAnswer) {
      setSliderValue(currentAnswer.value);
    }
  }, [currentAnswer]);

  const StyledLabels = (props: any) => (
    <Typography
      variant='body1'
      color={shouldGreyOutUntilAnswered ? 'text.secondary' : greenColors[700]}
      align={props.align}
      {...props}
      sx={{
        fontSize: {xs: '14px', sm: '18px'},
        lineHeight: 1.4,
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        hyphens: 'auto',
      }}
    />
  );

  const sliderStyles = {
    color: shouldGreyOutUntilAnswered ? '#c7c7c7' : greenColors[70],
    backgroundImage: 'none',
    '& .MuiSlider-thumb': {
      height: {xs: 28, sm: 32, md: 40, lg: 40},
      width: {xs: 28, sm: 32, md: 40, lg: 40},
      backgroundColor: isAnswered ? greenColors[700] : '#bdbdbd',
      '&:hover': {
        boxShadow: isAnswered ? 'none' : '0px 0px 0px 8px rgba(255, 255, 255, 0.70)',
        color: isAnswered ? greenColors[700] : greenColors[70],
        backgroundColor: greenColors[700],
      },
    },
    '& .MuiSlider-rail': {
      height: {xs: 10, sm: 12, md: 16, lg: 16},
      backgroundColor: 'transparent',
      backgroundImage: shouldGreyOutUntilAnswered
        ? 'linear-gradient(90deg, #d9d9d9 0%, #cfcfcf 50%, #d9d9d9 100%)'
        : `linear-gradient(90deg, ${greenColors[70]} 0%, ${greenColors[40]} 50%, ${greenColors[70]} 100%)`,
      opacity: 1,
      borderRadius: '0px',
    },
    '& .MuiSlider-track': {
      height: 16,
      color: 'transparent',
      backgroundImage: 'none',
    },
    '& .MuiSlider-mark': {
      opacity: 1,
      height: {xs: 22, sm: 26, md: 32, lg: 32},
      width: {xs: 22, sm: 26, md: 32, lg: 32},
      backgroundColor: shouldGreyOutUntilAnswered ? '#c4c4c4' : greenColors[70],
      borderRadius: '50%',
      marginLeft: {xs: -1.1, sm: -1.8, md: -2, lg: -2},
      '&:hover': {
        color: greenColors[70],
        backgroundColor: greenColors[70],
        zoom: {xs: 'none', lg: 1.2},
        ease: {xs: 'none', lg: 'ease'},
        transition: {xs: 'none', lg: 'all 0.3s ease'},
      },
    },
    '& .MuiSlider-markActive': {
      backgroundColor: shouldGreyOutUntilAnswered ? '#c4c4c4' : greenColors[70],
    },
  };

  return (
    <>
      <Box textAlign='center' mb={2}>
        <Typography
          variant='h1'
          color={shouldGreyOutUntilAnswered ? 'text.secondary' : greenColors[700]}
          lineHeight={1}
          sx={{
            fontSize: {xs: '1.5rem', sm: '2rem', md: '2.5rem'},
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto',
          }}
        >
          {data.title[currentLanguage]}
        </Typography>
      </Box>
      <Box
        sx={{
          height: 4,
          mx: {xs: 4, sm: 6, md: 8},
          mb: {xs: 1, sm: 2, md: 3},
          borderRadius: 999,
          background: shouldGreyOutUntilAnswered
            ? 'linear-gradient(90deg, #f3f3f3 0%, #d9d9d9 50%, #f3f3f3 100%)'
            : `linear-gradient(90deg, #ffffff 0%, ${orangeColors[900]} 50%, #ffffff 100%)`,
        }}
      />
      <Box
        sx={{
          mt: 1,
          px: {xs: 2, sm: 3, md: 4},
          py: {xs: 2, sm: 3},
          backgroundColor: shouldGreyOutUntilAnswered ? '#f5f5f5' : '#fff',
          borderRadius: 3,
          border: shouldGreyOutUntilAnswered ? '1px solid #dddddd' : `1px solid ${orangeColors[4]}`,
        }}
      >
        <Slider
          aria-label='Answer'
          step={data.scaleConfiguration.step}
          marks
          min={data.scaleConfiguration.minValue}
          max={data.scaleConfiguration.maxValue}
          valueLabelDisplay='auto'
          name={data.resourceId}
          value={sliderValue || 0}
          onChange={(event, newValue) => {
            setSliderValue(Array.isArray(newValue) ? newValue[0] : newValue);
            onAnswer(event);
          }}
          sx={sliderStyles}
        />
        <Box mt={2}>
          <Grid container columns={12} display='flex' justifyContent='space-between' columnSpacing={8}>
            <Grid
              item
              xs={6}
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              <StyledLabels align='left'>{data.scaleConfiguration.labelLeft[currentLanguage]}</StyledLabels>
            </Grid>
            <Grid
              item
              xs={6}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <StyledLabels align='right'>{data.scaleConfiguration.labelRight[currentLanguage]}</StyledLabels>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};
